import { createContext, ReactNode, useCallback, useContext, useEffect, useRef, useState } from 'react'
import ErrorComponent, { ErrorAction } from '../Dialogs/Forms/Error'
import GetCookie from '../globalHeader/common/getCookie'
import { ensureLogin, getUserData, guestRegistration, logout } from '../globalHeader/functions/ams-functions'
import {
    AreaType,
    GroupNames,
    LoggedInUserData,
    LoginState,
    Settings,
    UserData,
    UserGroup,
} from '../globalHeader/GlobalHeader'
import { AnalyticsEventMap, analyticsManager } from '../globalHeader/services/analytics/AnalyticsManager'
import { getHighestUserGroupName } from '../globalHeader/services/analytics/utils'
import { commonNavigate } from '../helpers/commonNavigate'
import { reportSharedError } from '../utils/errorReporting'
import { Dialogs, useDialogs } from './DialogsContext'

interface GlobalContextProps {
    userData: UserData | null
    setUserData: (user: UserData | null) => void
    isGuest: boolean
    userType: GroupNames | 'visitor'
    loginState: LoginState
    env: string
    area: AreaType
    pvpEmotes?: boolean
    googleClientId?: string
    googleClientIdAndroid?: string
    googleClientIdIos?: string
    facebookAppId?: string
    facebookApiVersion?: string
    appleClientId?: string
    appleRedirectURL?: string
    playAreaURL: string
    websiteURL: string
    forumsURL: string
    learningURL: string
    shopURL: string
    apiURL: string
    socialData: any
    setSocialData: (data: any) => void
    onErrorHandler: (action: ErrorAction) => void
    onLoginSuccessful: () => void
    onAuthClose: (analyticsEvent?: keyof AnalyticsEventMap) => void
    onGuestRegistration: () => Promise<void>
    onLoginStateChange: (data: LoggedInUserData) => void
    onSettingsChange: (data: Settings) => void
    /** Shows a toast in the host app. Absent hosts show no toast. */
    showSnackbar?: (message: string) => void
    ensureLoginCallback: () => Promise<void>
    logoutHandler: (reason?: 'explicit' | 'server_push') => Promise<void>
    navigate: (url: string) => void
}

const GlobalContext = createContext<GlobalContextProps | undefined>(undefined)

interface GlobalProviderProps {
    initUserData: UserData | null
    children: ReactNode
    area: AreaType
    pvpEmotes?: boolean
    env: string
    googleClientId?: string
    googleClientIdAndroid?: string
    googleClientIdIos?: string
    facebookAppId?: string
    facebookApiVersion?: string
    appleClientId?: string
    appleRedirectURL?: string
    playAreaURL: string
    websiteURL: string
    forumsURL: string
    learningURL: string
    shopURL: string
    apiURL: string
    onLoginStateChange: (data: LoggedInUserData) => void
    onSettingsChange: (data: Settings) => void
    /** Shows a toast in the host app. Absent hosts show no toast. */
    showSnackbar?: (message: string) => void
    navigate: (url: string) => void
}

export const GlobalProvider: React.FC<GlobalProviderProps> = (props) => {
    const {
        initUserData,
        area,
        pvpEmotes,
        children,
        env,
        googleClientId,
        googleClientIdAndroid,
        googleClientIdIos,
        facebookAppId,
        facebookApiVersion,
        appleClientId,
        appleRedirectURL,
        playAreaURL,
        websiteURL,
        forumsURL,
        learningURL,
        shopURL,
        apiURL,
        onLoginStateChange,
        onSettingsChange,
        showSnackbar,
        navigate,
    } = props
    const [userData, setUserData] = useState<UserData | null>(initUserData)
    const [loginState, setLoginState] = useState(!!initUserData ? LoginState.LOGGED_IN : LoginState.INITIALIZING)
    const [isGuest, setIsGuest] = useState<boolean>(
        userData?.groups.some((group: UserGroup) => group.name === GroupNames.GUEST) || false,
    )
    const [userType, setUserType] = useState<GroupNames | 'visitor'>(getHighestUserGroupName(userData?.groups || []))
    const [socialData, setSocialData] = useState<any>(null)
    const { registerDialog, unregisterDialog } = useDialogs()
    const ensuredRef = useRef<Boolean>(false)
    // Set when a boot check came back indeterminate and we deliberately did NOT
    // settle the session either way. The 'online' retry keys off this, not off
    // loginState: loginState is derived from userData and flips to NOT_LOGGED_IN
    // on mount, so a guard on it was false by the time any 'online' event fired.
    const awaitingRetryRef = useRef(false)

    useEffect(() => {
        setIsGuest(userData?.groups.some((group: UserGroup) => group.name === GroupNames.GUEST) || false)
        setUserType(getHighestUserGroupName(userData?.groups || []))
        setLoginState(!!userData ? LoginState.LOGGED_IN : LoginState.NOT_LOGGED_IN)
    }, [userData])

    // First Time Redirect
    useEffect(() => {
        if (userData) {
            const { first_time_login, user_config } = userData
            const { login_redirect_url } = user_config

            if (first_time_login && login_redirect_url) {
                setUserData({ ...userData, first_time_login: false })
                commonNavigate(login_redirect_url, navigate)
            }
        }
    }, [userData?.first_time_login, userData?.user_config])

    useEffect(() => {
        if (initUserData) {
            // quick fix for bi-directional data flow, in future should be adressed in a better way
            setUserData({ ...initUserData, newContent: userData?.newContent || initUserData.newContent })
        }
    }, [initUserData])

    // ---------- Auth Flow ----------
    const onErrorHandler = (action: ErrorAction) => {
        registerDialog(Dialogs.ERROR, <ErrorComponent errorAction={action} />)
    }

    const onLoginSuccessful = () => {
        unregisterDialog(Dialogs.SIGNIN)
        unregisterDialog(Dialogs.SIGNUP)
        unregisterDialog(Dialogs.SIGN_UP_SOCIAL)
        const token = GetCookie('jwt')
        const userDataResult = getUserData()
        setSocialData(null)
        setUserData(userDataResult.user)
        if (userDataResult && userDataResult.user) {
            onLoginStateChange({
                state: LoginState.LOGGED_IN,
                userData: userDataResult.user,
                token: token,
            })

            // const { first_time_login, user_config } = userDataResult.user
            // const { login_redirect_url } = user_config

            // if (first_time_login && login_redirect_url) {
            //     const url = login_redirect_url
            //     commonNavigate(url, navigate)
            // }
        }
    }

    const onGuestRegistration = async () => {
        try {
            const result = await guestRegistration()
            // const { first_time_login, user_config } = result.user
            // const { login_redirect_url } = user_config

            // if (first_time_login && login_redirect_url) {
            //     const url = login_redirect_url
            //     commonNavigate(url, navigate)
            // }

            if (!result.error) {
                onLoginSuccessful()
                if (result.joined) return
                analyticsManager.dispatchEvent('completeSignup', {
                    authType: 'guest',
                    country: 'NA',
                    userId: result.user.id,
                    userName: result.user.user_name,
                })
            } else {
                onErrorHandler(ErrorAction.GUEST_REGISTRATION)
            }
        } catch (e) {
            reportSharedError('Guest registration failed', e, { flow: 'auth' })
        }
    }

    const onAuthClose = (analyticsEvent: keyof AnalyticsEventMap = 'loginDismissed') => {
        analyticsManager.dispatchEvent(analyticsEvent, {})
    }

    const ensureLoginCallback = useCallback(async () => {
        const result = await ensureLogin()
        // Stale = a newer login/logout superseded this check; there's nothing to
        // retry and nothing to apply.
        if (result.stale) return
        awaitingRetryRef.current = result.indeterminate

        if (result.user) {
            setUserData(result.user)
            const token = GetCookie('jwt')

            // const { first_time_login, user_config } = userDataResult
            // const { login_redirect_url } = user_config

            // if (first_time_login && login_redirect_url) {
            //     const url = login_redirect_url
            //     commonNavigate(url, navigate)
            // }

            onLoginStateChange({
                state: LoginState.LOGGED_IN,
                userData: result.user,
                token: token,
            })
        } else if (result.indeterminate) {
            // ensureLogin already retried once internally (network blip) — the
            // cookie is untouched. Emitting NOT_LOGGED_IN here would cascade into
            // the host's full session teardown (App.tsx's resetSessionState, wired
            // unconditionally off that state) for a merely unconfirmed check, not
            // a real logout — and this effect can run again at any time this
            // component re-mounts with no confirmed session yet. Do nothing; the
            // 'online' listener below retries once connectivity genuinely returns.
            return
        } else if (area === AreaType.PLAY_AREA) {
            onGuestRegistration()
        } else {
            setUserData(null)
            onLoginStateChange({
                state: LoginState.NOT_LOGGED_IN,
                userData: null,
                token: '',
            })
        }
    }, [])

    // Auto login
    useEffect(() => {
        if (!initUserData) {
            if (ensuredRef.current === true) return
            ensuredRef.current = true
            ensureLoginCallback()
        }
    }, [])

    // A boot check that came back indeterminate (network was down) deliberately
    // settles nothing — see the indeterminate branch above. Retry once the
    // browser reports connectivity is back, rather than leaving a web user
    // (who has no appResume) identity-less until they manually reload.
    useEffect(() => {
        const retryOnReconnect = () => {
            if (awaitingRetryRef.current) ensureLoginCallback()
        }
        window.addEventListener('online', retryOnReconnect)
        return () => window.removeEventListener('online', retryOnReconnect)
    }, [ensureLoginCallback])

    const logoutHandler = async (reason: 'explicit' | 'server_push' = 'explicit') => {
        try {
            await logout(reason)
            analyticsManager.dispatchEvent('logout', {})
            onLoginStateChange({
                state: LoginState.INITIALIZING,
                userData: null,
                token: '',
            })
            if (area === AreaType.PLAY_AREA) {
                onGuestRegistration()
            } else {
                onLoginStateChange({
                    state: LoginState.NOT_LOGGED_IN,
                    userData: null,
                    token: '',
                })
                setUserData(null)
            }
        } catch (e) {
            reportSharedError('Logout failed', e, { flow: 'auth' })
        }
    }

    // ---------- End Auth Flow ----------

    const context = {
        userData,
        setUserData,
        isGuest,
        userType,
        loginState,
        area,
        pvpEmotes,
        env,
        googleClientId,
        googleClientIdAndroid,
        googleClientIdIos,
        facebookAppId,
        facebookApiVersion,
        appleClientId,
        appleRedirectURL,
        playAreaURL,
        websiteURL,
        forumsURL,
        learningURL,
        shopURL,
        apiURL,
        socialData,
        setSocialData,
        onErrorHandler,
        onLoginSuccessful,
        onSettingsChange,
        showSnackbar,
        onAuthClose,
        onGuestRegistration,
        onLoginStateChange,
        ensureLoginCallback,
        logoutHandler,
        navigate,
    }

    return <GlobalContext.Provider value={context}>{children}</GlobalContext.Provider>
}

export const useGlobalContext = () => {
    const context = useContext(GlobalContext)
    if (!context) {
        throw new Error('useGlobalContext must be used within a GlobalProvider')
    }
    return context
}
